import { useCallback } from "react";
import { NavigateOptions, useLocation, useNavigate } from "react-router-dom";

/**
 * Drop-in replacement for `useNavigate` that carries the current URL query
 * string onto the destination so landing-page params survive in-app
 * navigation. Supports `to` values with a hash fragment; a `to` that already
 * carries its own query string is left untouched.
 */
export const useNavigateWithSearch = () => {
    const navigate = useNavigate();
    const { search } = useLocation();

    return useCallback(
        (to: string, options?: NavigateOptions) => {
            let target = to;
            if (search && !to.includes("?")) {
                const hashIndex = to.indexOf("#");
                const pathname = hashIndex === -1 ? to : to.slice(0, hashIndex);
                const hash = hashIndex === -1 ? "" : to.slice(hashIndex);
                target = `${pathname}${search}${hash}`;
            }
            // Preserve call arity: an explicit `undefined` options argument would
            // change navigate's observed signature (and strict call assertions).
            if (options === undefined) {
                navigate(target);
            } else {
                navigate(target, options);
            }
        },
        [navigate, search]
    );
};
